import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

type DeclarationType = 'tva_mensuelle' | 'is_annuelle' | 'ras_periodique';

interface VatLine {
  amount_ht_tnd: string;
  vat_rate: 0 | 7 | 13 | 19;
  vat_exemption_code: string;
}

interface FiscalisteResult {
  declaration_type: DeclarationType;
  period_label: string;
  summary: Record<string, number>;
  next_deadline: string;
  legal_basis: Array<{ source_type: string; source_id: string; reference: string; fetched_at: string }>;
}

const EMPTY_VAT_LINE: VatLine = { amount_ht_tnd: '0.000', vat_rate: 19, vat_exemption_code: '' };

export function FiscalistePage() {
  const [type, setType] = useState<DeclarationType>('tva_mensuelle');

  // TVA
  const [period, setPeriod] = useState('2026-03');
  const [ventes, setVentes] = useState<VatLine[]>([{ amount_ht_tnd: '10000.000', vat_rate: 19, vat_exemption_code: '' }]);
  const [achats, setAchats] = useState<VatLine[]>([{ amount_ht_tnd: '4000.000', vat_rate: 19, vat_exemption_code: '' }]);
  const [proRata, setProRata] = useState('');
  const [creditReporte, setCreditReporte] = useState('0.000');

  // IS
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [fiscalStart, setFiscalStart] = useState('2026-01-01');
  const [fiscalEnd, setFiscalEnd] = useState('2026-12-31');
  const [taxableIncome, setTaxableIncome] = useState('100000.000');
  const [sectorCode, setSectorCode] = useState('');
  const [acomptesPaid, setAcomptesPaid] = useState('5000.000');

  // RAS
  const [rasDate, setRasDate] = useState('2026-03-15');
  const [rasType, setRasType] = useState<'honoraires' | 'loyers' | 'dividendes' | 'non_residents'>('honoraires');
  const [rasGross, setRasGross] = useState('1000.000');
  const [conventionRate, setConventionRate] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function updateLine(set: typeof setVentes, idx: number, patch: Partial<VatLine>) {
    set((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      let payload: Record<string, unknown>;
      if (type === 'tva_mensuelle') {
        payload = {
          declaration_type: 'tva_mensuelle',
          fiscal_period: period,
          ventes: ventes.map((l) => ({
            amount_ht_millimes: tndStringToMillimes(l.amount_ht_tnd),
            vat_rate: l.vat_rate,
            ...(l.vat_exemption_code ? { vat_exemption_code: l.vat_exemption_code } : {}),
          })),
          achats: achats.map((l) => ({
            amount_ht_millimes: tndStringToMillimes(l.amount_ht_tnd),
            vat_rate: l.vat_rate,
            ...(l.vat_exemption_code ? { vat_exemption_code: l.vat_exemption_code } : {}),
          })),
          credit_tva_reporte_millimes: tndStringToMillimes(creditReporte),
          ...(proRata ? { prorata_rate: parseFloat(proRata) } : {}),
        };
      } else if (type === 'is_annuelle') {
        payload = {
          declaration_type: 'is_annuelle',
          fiscal_year: fiscalYear,
          fiscal_year_start: fiscalStart,
          fiscal_year_end: fiscalEnd,
          taxable_income_millimes: tndStringToMillimes(taxableIncome),
          acomptes_already_paid_millimes: tndStringToMillimes(acomptesPaid),
          ...(sectorCode ? { sector_code: sectorCode } : {}),
        };
      } else {
        payload = {
          declaration_type: 'ras_periodique',
          fiscal_reference_date: rasDate,
          ras_type: rasType,
          gross_amount_millimes: tndStringToMillimes(rasGross),
          ...(conventionRate ? { convention_rate_override: parseFloat(conventionRate) } : {}),
        };
      }
      const res = await api.invokeAgent('fiscaliste', { payload, fiscal_reference_date: type === 'is_annuelle' ? fiscalEnd : rasDate });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Fiscaliste"
      module="Fiscal · Phase 2"
      model="Sonnet 4.6"
      description={
        <>
          Déclarations fiscales tunisiennes — TVA mensuelle (collectée vs déductible),
          IS annuelle (taux par secteur via ontologie LF 2026), RAS périodique (honoraires,
          loyers, dividendes, non-résidents avec convention bilatérale).
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Type de déclaration">
          <div className="flex flex-wrap gap-2">
            {(['tva_mensuelle', 'is_annuelle', 'ras_periodique'] as DeclarationType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`px-3 py-1.5 rounded border text-sm transition ${
                  type === t
                    ? 'bg-ink text-cream border-ink'
                    : 'bg-white text-ink border-border hover:border-gold'
                }`}
              >
                {labelType(t)}
              </button>
            ))}
          </div>
        </FormCard>

        {type === 'tva_mensuelle' && (
          <>
            <FormCard title="Période & paramètres">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Période (YYYY-MM)" hint="Mois de la déclaration">
                  <input className={inputCls + ' font-mono'} value={period} onChange={(e) => setPeriod(e.target.value)} required />
                </Field>
                <Field label="Prorata déduction (0..1)" hint="Optionnel — Code TVA Art. 9">
                  <input className={inputCls + ' font-mono'} value={proRata} onChange={(e) => setProRata(e.target.value)} placeholder="0.65" />
                </Field>
                <Field label="Crédit TVA reporté (TND)" hint="Du mois précédent">
                  <input className={inputCls + ' font-mono'} value={creditReporte} onChange={(e) => setCreditReporte(e.target.value)} />
                </Field>
              </div>
            </FormCard>

            <VatLinesCard
              title={`Ventes (${ventes.length}) — TVA collectée`}
              lines={ventes}
              onAdd={() => setVentes((p) => [...p, { ...EMPTY_VAT_LINE }])}
              onRemove={(i) => setVentes((p) => p.filter((_, idx) => idx !== i))}
              onUpdate={(i, patch) => updateLine(setVentes, i, patch)}
            />
            <VatLinesCard
              title={`Achats (${achats.length}) — TVA déductible`}
              lines={achats}
              onAdd={() => setAchats((p) => [...p, { ...EMPTY_VAT_LINE }])}
              onRemove={(i) => setAchats((p) => p.filter((_, idx) => idx !== i))}
              onUpdate={(i, patch) => updateLine(setAchats, i, patch)}
            />
          </>
        )}

        {type === 'is_annuelle' && (
          <FormCard title="Exercice fiscal & assiette">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Année fiscale">
                <input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} />
              </Field>
              <Field label="Secteur (optionnel)" hint="industrie_export → 10% · banque_assurance → 25% · hydrocarbures → 35% · sinon 15%">
                <select className={inputCls} value={sectorCode} onChange={(e) => setSectorCode(e.target.value)}>
                  <option value="">Standard (15%)</option>
                  <option value="industrie_export">Industrie export (10%)</option>
                  <option value="banque_assurance">Banque/Assurance (25%)</option>
                  <option value="hydrocarbures">Hydrocarbures (35%)</option>
                </select>
              </Field>
              <Field label="Date de début exercice">
                <input type="date" className={inputCls} value={fiscalStart} onChange={(e) => setFiscalStart(e.target.value)} required />
              </Field>
              <Field label="Date de fin exercice">
                <input type="date" className={inputCls} value={fiscalEnd} onChange={(e) => setFiscalEnd(e.target.value)} required />
              </Field>
              <Field label="Bénéfice imposable (TND)">
                <input className={inputCls + ' font-mono'} value={taxableIncome} onChange={(e) => setTaxableIncome(e.target.value)} required />
              </Field>
              <Field label="Acomptes déjà versés (TND)">
                <input className={inputCls + ' font-mono'} value={acomptesPaid} onChange={(e) => setAcomptesPaid(e.target.value)} />
              </Field>
            </div>
          </FormCard>
        )}

        {type === 'ras_periodique' && (
          <FormCard title="Retenue à la source">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Date de référence">
                <input type="date" className={inputCls} value={rasDate} onChange={(e) => setRasDate(e.target.value)} required />
              </Field>
              <Field label="Type de RAS">
                <select className={inputCls} value={rasType} onChange={(e) => setRasType(e.target.value as typeof rasType)}>
                  <option value="honoraires">Honoraires (10%)</option>
                  <option value="loyers">Loyers (15%)</option>
                  <option value="dividendes">Dividendes (10%)</option>
                  <option value="non_residents">Non-résidents (25% ou convention)</option>
                </select>
              </Field>
              <Field label="Montant brut (TND)">
                <input className={inputCls + ' font-mono'} value={rasGross} onChange={(e) => setRasGross(e.target.value)} required />
              </Field>
              {rasType === 'non_residents' && (
                <Field label="Taux conventionnel (0..1)" hint="Optionnel — convention fiscale bilatérale">
                  <input className={inputCls + ' font-mono'} value={conventionRate} onChange={(e) => setConventionRate(e.target.value)} placeholder="0.10" />
                </Field>
              )}
            </div>
          </FormCard>
        )}

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Calcul…' : 'Calculer la déclaration'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <FiscalisteResultView result={result as FiscalisteResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function VatLinesCard({
  title,
  lines,
  onAdd,
  onRemove,
  onUpdate,
}: {
  title: string;
  lines: VatLine[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onUpdate: (i: number, patch: Partial<VatLine>) => void;
}) {
  return (
    <FormCard
      title={title}
      action={
        <button type="button" onClick={onAdd} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">
          + Ajouter une ligne
        </button>
      }
    >
      <div className="space-y-3">
        {lines.map((l, idx) => (
          <div key={idx} className="border border-border rounded p-3 bg-cream/30 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
            <Field label="HT (TND)"><input className={inputCls + ' font-mono'} value={l.amount_ht_tnd} onChange={(e) => onUpdate(idx, { amount_ht_tnd: e.target.value })} /></Field>
            <Field label="Taux">
              <select className={inputCls} value={l.vat_rate} onChange={(e) => onUpdate(idx, { vat_rate: parseInt(e.target.value, 10) as 0 | 7 | 13 | 19 })}>
                <option value={19}>19%</option>
                <option value={13}>13%</option>
                <option value={7}>7%</option>
                <option value={0}>0%</option>
              </select>
            </Field>
            {l.vat_rate === 0 && (
              <Field label="Code exo." hint="ex. TVA_EXP_004">
                <input className={inputCls + ' font-mono'} value={l.vat_exemption_code} onChange={(e) => onUpdate(idx, { vat_exemption_code: e.target.value })} />
              </Field>
            )}
            {lines.length > 1 && (
              <button type="button" onClick={() => onRemove(idx)} className="text-xs text-red-700 hover:text-red-900 px-2 self-end">
                Supprimer
              </button>
            )}
          </div>
        ))}
      </div>
    </FormCard>
  );
}

function FiscalisteResultView({ result }: { result: FiscalisteResult }) {
  const entries = Object.entries(result.summary);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="px-2 py-1 rounded bg-ink text-cream text-xs uppercase tracking-wider">{labelType(result.declaration_type)}</span>
        <span className="text-sm">Période <strong>{result.period_label}</strong></span>
        <span className="text-xs text-taupe">Prochaine échéance : <strong className="font-mono">{result.next_deadline}</strong></span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {entries.map(([key, value]) => {
          const isMillimes = key.endsWith('_millimes');
          const isRate = key.endsWith('_x10000');
          const display = isMillimes
            ? formatMillimes(value)
            : isRate
            ? `${(value / 100).toFixed(2)}%`
            : String(value);
          return (
            <div key={key} className="p-3 rounded border border-border bg-white">
              <div className="text-xs uppercase tracking-wider text-taupe">{prettyKey(key)}</div>
              <div className="font-mono text-lg">{display}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function labelType(t: DeclarationType): string {
  return t === 'tva_mensuelle' ? 'TVA mensuelle' : t === 'is_annuelle' ? 'IS annuelle' : 'RAS périodique';
}

function prettyKey(k: string): string {
  return k
    .replace(/_millimes$/, '')
    .replace(/_x10000$/, ' (taux)')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
